import { TextureLoader, Material, Mesh, MeshStandardMaterial, ShaderChunk, sRGBEncoding, WebGLRenderer } from "three"

// NOTE Usage:
//  remapMaterialUVs(child.material, {
//    'emissiveMap': 'texcoord_2', // <- fourth UV map
//    'roughnessMap': 'texcoord_3' // <- fifth UV map
//  });

// remapMaterialUVs is under MIT license - https://codepen.io/mjurczyk/pen/PoRrmvy?editors=0010

const remapMaterialUVs = (material, remapUVs, {
    uvAttributePrefix,
    uvAttributeOffset
} = {}) => {
    const _uvAttributePrefix = uvAttributePrefix || 'texcoord_';
    const _uvAttributeOffset = 2;

    material.customProgramCacheKey = () => Math.random();
    material.onBeforeCompile = (shader, context) => {
        const resolveIncludes = (shader) => {
            // NOTE Straight from three/WebGLProgram.js
            const includePattern = /^[ \t]*#include +<([\w\d./]+)>/gm;

            return shader.replace(includePattern, (match, include) => {
                const string = ShaderChunk[include];

                if (!string) {
                    return;
                }

                return resolveIncludes(string);
            });
        };

        let maxTexCoordIndex = 0;
        const texCoordSwaps = [];

        Object.entries(remapUVs).forEach(([textureType, uvMap]) => {
            if (!uvMap.startsWith(_uvAttributePrefix)) {
                console.warn('Invalid UVMap name', uvMap);

                return;
            }

            const texCoordIndex = parseFloat(uvMap.split(new RegExp(`${_uvAttributePrefix}\\D*`, 'gi')).join(''));

            maxTexCoordIndex = Math.max(texCoordIndex, maxTexCoordIndex);
            texCoordSwaps[textureType] = texCoordIndex;
        });

        if (maxTexCoordIndex - 1 <= 0) {
            return;
        }

        shader.vertexShader = shader.vertexShader.replace(`#include <uv2_pars_vertex>`, `
        ${Array(maxTexCoordIndex - 1).fill(0).map((_, index) => `
          attribute vec2 ${_uvAttributePrefix}${index + _uvAttributeOffset};
          varying vec2 vTexCoord${index + _uvAttributeOffset};
        `).join('\n')}
  
        #include <uv2_pars_vertex>
      `).replace(`#include <uv2_vertex>`, `
        ${Array(maxTexCoordIndex - 1).fill(0).map((_, index) => `
          vTexCoord${index + _uvAttributeOffset} = ( vec3( ${_uvAttributePrefix}${index + _uvAttributeOffset}, 1 ) ).xy;
        `).join('\n')}
  
        #include <uv2_vertex>
      `);

        shader.fragmentShader = shader.fragmentShader.replace(`#include <uv2_pars_fragment>`, `
        ${Array(maxTexCoordIndex - 1).fill(0).map((_, index) => `
          varying vec2 vTexCoord${index + _uvAttributeOffset};
        `).join('\n')}
  
        #include <uv2_pars_fragment>
      `);

        shader.vertexShader = resolveIncludes(shader.vertexShader);
        shader.fragmentShader = resolveIncludes(shader.fragmentShader);

        Object.entries(texCoordSwaps).forEach(([textureType, texCoordIndex]) => {
            shader.fragmentShader = shader.fragmentShader.replaceAll(
                `texture2D( ${textureType}, vUv )`,
                `texture2D( ${textureType}, vTexCoord${texCoordIndex} )`
            ).replaceAll(
                `texture2D( ${textureType}, vUv2 )`,
                `texture2D( ${textureType}, vTexCoord${texCoordIndex} )`
            );
        });
    };
};

/**
 * 
 * @param {Mesh} mesh 
 * @param {Material} material 
 * @param {TextureInitializer} textureInitializer 
 * @returns 
 */
function setMaterialTextureOnMeshRender(mesh, material, texture, textureInitializer, mapLevel) {
    if(mesh.userData.materialSwapInProgress) {
        console.warn('setMaterialOnRender called on mesh with material swap in progress', mesh)
        return
    }
    mesh.userData.materialSwapInProgress = true
    mesh.onBeforeRender = function() {
        textureInitializer.init(texture).then(() => {
            material.map = texture

            if (mesh.mapLevel != mapLevel)
                return

            mesh.material = material
            this.userData.materialSwapInProgress = false
        });
        delete this.onBeforeRender
    }
}

function setMaterialOnRender(mesh, material, mapLevel) {
    if(mesh.userData.materialSwapInProgress) {
        console.warn('setMaterialOnRender called on mesh with material swap in progress', mesh)
        return
    }
    mesh.userData.materialSwapInProgress = true
    mesh.onBeforeRender = function() {
        if (mesh.mapLevel != mapLevel)
            return

        this.material = material
        delete this.onBeforeRender
        this.userData.materialSwapInProgress = false
    }
}

/**
 * Initializes textures with a delay to avoid stuttering
 */
export class TextureInitializer {
    /**
     * 
     * @param {WebGLRenderer} renderer 
     * @param {number} frameDelay - number of frames in between each initialization
     */
    constructor(renderer, frameDelay) {
        /**
         * @type WebGLRenderer
         */
        this.renderer = renderer
        this.queue = []
        this.frameDelay = frameDelay
        this.frameCounter = 0
    }
    async init(texture) {
        let resolve, reject
        const promise =  new Promise((res, rej) => {
            resolve = res
            reject = rej
        })
        this.queue.push({
            resolve,
            reject,
            texture
        })
        return promise
    }
    process({resolve, reject, texture}) {
        this.renderer.initTexture(texture)
        resolve()
    }
    update() {
        if (this.queue.length === 0) {
            return
        }

        this.frameCounter += 1
        if (this.frameCounter < this.frameDelay) {
            return
        }
        
        this.frameCounter = 0

        this.process(this.queue.pop())
    }
}

export class WebMultiMaterial {
    /**
     * 
     * @param {Material} initialMaterial 
     * @param {Array} webTexData 
     * @param {TextureLoader} textureLoader
     * @param {TextureInitializer} textureInitializer
     */
    constructor(initialMaterial, webTexData, textureLoader, textureInitializer) {
        /**
         * @type Material
         */
        this.initialMaterial = initialMaterial
        /**
         * @type Array
         */
        this.webTexData = webTexData
        /**
         * @type Array< Material | undefined>
         */
        this.materials = Array(webTexData.length)
        /**
         * @type Array<Promise>
         */
        this.promises = Array(webTexData.length)
        /**
         * @type Number
         */
        this.activeMaterial = 0
        /**
         * @type TextureLoader
         */
        this.loader = textureLoader
        /**
         * @type TextureInitializer
         */
        this.textureInitializer = textureInitializer
        /**
         * @type boolean
         */
        this.aborted = false
    }
    /**
     * Apply the active material to a mesh
     * @param {Mesh} mesh 
     */
    async applyTo(mesh) {
        mesh.mapLevel = this.activeMaterial

        if (mesh.userData.materialSwapInProgress) {
            mesh.userData.materialSwapInProgress = false
        }

        if (this.activeMaterial === 0) {
            setMaterialOnRender(mesh, this.initialMaterial, mesh.mapLevel)
            return true
        }

        if (this.materials[this.activeMaterial] !== undefined) {
            setMaterialOnRender(mesh, this.materials[this.activeMaterial], mesh.mapLevel)
            return true
        }

        const texture = await this.promises[this.activeMaterial]
        texture.flipY = false
        texture.encoding = sRGBEncoding

        if (this.aborted)
            return false

        const material = new MeshStandardMaterial({
            color: "#FFFFFF",
            emissive: "#000000"
        });

        remapMaterialUVs(material, {
            map: `texcoord_${this.activeMaterial+1}`
        })

        this.materials[this.activeMaterial] = material

        if (mesh.mapLevel !== this.activeMaterial)
            return false

        setMaterialTextureOnMeshRender(mesh, material, texture, this.textureInitializer, mesh.mapLevel)
        return true
    }
    /**
     * Change active material
     * 
     * Triggers a load of that material if it hasn't been loaded yet
     * @param {number} index 
     */
    swap(index) {
        this.activeMaterial = index

        // Short circuit if index is 0, indicating initialMaterial
        if (index === 0)
            return

        // Short circuit if a load for this material has already been started
        if (this.promises[index] !== undefined)
            return

        // Begin loading the texture
        this.promises[index] = new Promise((resolve, reject) => {
            const url = this.webTexData[index - 1].url
            this.loader.load(url, resolve, null, reject)
        })
    }

    /**
     * Stop all ongoing asynchronous operations, or cause them to have no effect when finished
     * 
     * After calling this, the object becomes useless and should be discarded
     */
    abort() {
        this.aborted = true
    }
}